import { Brain } from '../bots/brain.ts';
import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill';
import { DEATH_TEXT, DEATH_TITLE, SAB_KINDS, TICK_HZ, audience, type SabKind } from '../shared/constants.ts';
import { isCountryCode, withFlag } from '../shared/country.ts';
import type { ClientDevice, GameSync, Leaderboards, NetEvent, OverInfo, ReplayData, Rival, ServerMsg, VoteState, Who } from '../shared/protocol.ts';
import { Sim, type SabEvent } from '../shared/sim.ts';
import { Input } from './input.ts';
import { Net, type NetState } from './net.ts';
import { Renderer, type DrawOpts } from './render.ts';
import { Sound } from './sound.ts';
import { Ui, type OverlayView, type ScreenAction, type StatusView } from './ui.ts';

/** Spectators replay the player's input stream this many ticks (~250 ms) behind, to absorb jitter. */
const SPECTATOR_BUFFER = 15;
/** After a death, let the splat play out before covering the screen with the result. */
const DEATH_PAUSE = 1300;
/** Shorter for spectators: the next run often starts within a couple of seconds. */
const DEATH_PAUSE_SPECTATOR = 700;
/** How long "GO!" stays up after the countdown. */
const GO_MS = 800;
/** A replay starts with the same 3-2-1 as a live run. */
const REPLAY_COUNTDOWN = 3000;
/** "Skip to the end" lands this long before the death. */
const SKIP_TO_END_TICKS = 4 * TICK_HZ;


interface View {
  gid: number;
  player: string;
  /** Player name with their country flag, for display. */
  label: string;
  mine: boolean;
  sim: Sim;
  /** The previous players to beat, drawn as lines on the course. */
  rivals: Rival[];
  inputs: number[];
  events: Map<number, SabEvent[]>;
  /** performance.now() at tick 0. */
  startAt: number;
  over: OverInfo | null;
  /** performance.now() when the run was first seen over (0 while it runs). */
  endedAt: number;
  // Sound cues already played: last countdown number (0 once GO! has sounded), coins, rivals beaten, the splat.
  beeped: number | null;
  coinsHeard: number;
  beatHeard: number;
  splatted: boolean;
  acc: number;
  buffering: boolean;
  // Player-only bookkeeping.
  sent: number;
  netEvents: NetEvent[];
  incoming: SabEvent[];
  seenSabs: Set<number>;
}

const store = {
  get(k: string): string | null {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set(k: string, v: string): void {
    try {
      localStorage.setItem(k, v);
    } catch {
      /* private mode: identity just won't survive a reload */
    }
  },
};

// Windows has no flag emoji: it gets a small web font instead (CSS lists it first, for flags only).
polyfillCountryFlagEmojis('Twemoji Country Flags', '/fonts/TwemojiCountryFlags.woff2');

/** Local dev only (the server ignores it from real IPs): `?cc=JP` to see yourself as Japanese. */
const ccOverride = new URLSearchParams(location.search).get('cc')?.toUpperCase();

/** Device details sent with hello (recorded with the visit). The phone model needs Chrome over HTTPS. */
const device: ClientDevice = {
  w: screen.width,
  h: screen.height,
  dpr: devicePixelRatio,
  touch: navigator.maxTouchPoints,
  tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
};
type UaData = { getHighEntropyValues(hints: string[]): Promise<{ model?: string; platformVersion?: string }> };
(navigator as Navigator & { userAgentData?: UaData }).userAgentData
  ?.getHighEntropyValues(['model', 'platformVersion'])
  .then((v) => {
    device.model = v.model || undefined;
    device.osv = v.platformVersion || undefined;
  })
  .catch(() => {});

const sound = new Sound(store.get('glope.muted') === '1');

let myId = store.get('glope.id') ?? randomId();
let myName = store.get('glope.name') ?? '';
store.set('glope.id', myId);

let netState: NetState = 'connecting';
let watchers = 0;
let queueLen = 0;
let pos: number | null = null;
let etaAt = 0;
let turnUntil = 0;
let readySent = false;
let missed = false;
let next: Who | null = null;
let votes: VoteState = { v: { speed: 0, rain: 0, dark: 0, flip: 0 }, th: 3, cd: 0 };
let cdUntil = 0;
let myVote: SabKind | null = null;
let boards: Leaderboards = { day: [] };
/** Your latest recorded run, for the "Share my last run" button (kept across visits: links don't expire). */
let lastRun: LastRun | null = loadLastRun();
let view: View | null = null;
/** A recorded run playing instead of the live one (opened from a /r/<id> link). `n` counts restarts. */
let replay: { data: ReplayData; view: View; n: number } | null = null;
let idle: { sim: Sim; brain: Brain; deadAt: number; acc: number } | null = null;

const ui = new Ui({
  chat: (m) => net.send({ t: 'chat', m }),
  vote: (kind) => {
    if (net.send({ t: 'vote', kind })) myVote = kind;
    refresh();
  },
  ready,
  join,
  leave: () => net.send({ t: 'leave' }),
  share: (rid, text) => void share(rid, text),
  watch,
  shareLast: () => {
    if (lastRun) void share(lastRun.rid, lastRun.text);
  },
  watchLast: () => {
    if (lastRun) watch(lastRun.rid);
  },
  mute: () => {
    sound.muted = !sound.muted;
    store.set('glope.muted', sound.muted ? '1' : '0');
    ui.setMuted(sound.muted);
  },
});
ui.setMuted(sound.muted);
ui.setLastRun(lastRun);
const input = new Input(ui.controlButtons);
const renderer = new Renderer(ui.canvas);
/** Said hello once already: later hellos are reconnects (which don't put us back in line). */
let greeted = false;
const net = new Net(onMessage, (s) => {
  netState = s;
  if (s === 'open') {
    net.send({
      t: 'hello',
      id: myId,
      name: myName || undefined,
      gid: view?.mine && !view.over && view.sim.alive ? view.gid : null,
      cc: isCountryCode(ccOverride) ? ccOverride : undefined,
      dev: device,
      join: !greeted || undefined,
    });
    greeted = true;
  }
  refresh();
});
net.connect();
setInterval(flushInputs, 50);
const replayLink = /^\/r\/([A-Za-z0-9]{8})$/.exec(location.pathname);
if (replayLink) void openReplay(replayLink[1]);

function ready(): void {
  if (net.send({ t: 'ready' })) readySent = true;
  unlockAudio();
  refresh();
}

function join(): void {
  net.send({ t: 'queue' });
  missed = false;
  unlockAudio();
  refresh();
}

// ------------------------------------------------------------------ network

function onMessage(msg: ServerMsg): void {
  const now = performance.now();
  switch (msg.t) {
    case 'welcome':
      myId = msg.id;
      myName = msg.name;
      store.set('glope.id', myId);
      store.set('glope.name', myName);
      ui.setMe(myName, msg.cc);
      break;
    case 'snapshot': {
      watchers = msg.watchers;
      queueLen = msg.queue;
      pos = msg.pos;
      next = msg.next;
      votes = msg.votes;
      cdUntil = now + msg.votes.cd;
      boards = msg.lb;
      ui.setLb(boards);
      ui.setChat(msg.chat);
      ui.setStats(watchers, queueLen);
      const g = msg.game;
      if (view?.mine && !view.over && (!g || g.gid !== view.gid)) {
        // Our run ended while we were disconnected.
        view.over = { g: view.gid, player: view.player, score: view.sim.score, dist: view.sim.distance, coins: view.sim.coins, reason: 'timeout', tick: view.sim.tick };
      }
      if (g && !(view && view.gid === g.gid && view.mine)) view = makeView(g);
      else if (!g && view && !view.mine && !view.over) view = null;
      break;
    }
    case 'stats':
      watchers = msg.watchers;
      queueLen = msg.queue;
      ui.setStats(watchers, queueLen);
      break;
    case 'pos':
      pos = msg.pos;
      queueLen = msg.len;
      etaAt = msg.pos ? now + msg.eta * 1000 : 0;
      break;
    case 'turn':
      turnUntil = now + msg.deadlineIn;
      readySent = false;
      missed = false;
      attention();
      break;
    case 'missed':
      turnUntil = 0;
      missed = true;
      break;
    case 'next':
      next = msg.who;
      break;
    case 'game':
      turnUntil = 0;
      readySent = false;
      myVote = null;
      next = null;
      view = makeView(msg);
      if (view.mine) {
        closeReplay();
        navigator.vibrate?.(200); // (the countdown beeps take it from here)
      }
      break;
    case 'f': {
      const v = view;
      if (!v || v.mine || v.gid !== msg.g || msg.f > v.inputs.length) break;
      const known = v.inputs.length;
      for (let k = known - msg.f; k < msg.i.length; k++) v.inputs.push(msg.i[k]);
      addEvents(v, msg.e.filter((e) => e.tick >= known));
      break;
    }
    case 'over':
      if (view && view.gid === msg.g) {
        view.over = msg;
        if (view.mine && msg.rid) {
          lastRun = { rid: msg.rid, text: `I ${DEATH_TEXT[msg.reason] ?? 'died'} at ${msg.dist} m ${audience(watchers)} on GLOPE 💀`, score: msg.score, dist: msg.dist };
          store.set('glope.lastRun', JSON.stringify(lastRun));
          ui.setLastRun(lastRun);
        }
      }
      myVote = null;
      break;
    case 'resume':
      if (view?.mine && view.gid === msg.gid) view.sent = msg.tick;
      break;
    case 'sab':
      if (view?.mine && !view.seenSabs.has(msg.id)) {
        view.seenSabs.add(msg.id);
        view.incoming.push({ id: msg.id, kind: msg.kind });
        navigator.vibrate?.([60, 40, 60]);
      }
      break;
    case 'chat':
      ui.addChat(msg);
      break;
    case 'votes':
      votes = msg;
      cdUntil = now + msg.cd;
      if (myVote && !msg.v[myVote]) myVote = null;
      break;
    case 'lb':
      boards = msg;
      ui.setLb(boards);
      break;
    case 'err':
      if (msg.code === 'replaced') {
        net.stop();
        ui.setOverlay({ title: 'OPENED ELSEWHERE', text: 'GLOPE is open in another tab or device.', action: { label: 'USE HERE', run: () => location.reload() } });
        return;
      }
      if (msg.msg) ui.toast(msg.msg);
      break;
  }
  refresh();
}

function makeView(g: GameSync): View {
  const v: View = {
    gid: g.gid,
    player: g.player,
    label: withFlag(g.player, g.cc),
    mine: !!g.you,
    sim: new Sim(g.seed, g.rivals.map((r) => r.dist)),
    rivals: g.rivals,
    inputs: g.inputs.slice(),
    events: new Map(),
    startAt: performance.now() + g.startIn,
    over: null,
    endedAt: 0,
    beeped: null,
    coinsHeard: 0,
    beatHeard: 0,
    splatted: false,
    acc: 0,
    buffering: true,
    sent: 0,
    netEvents: [],
    incoming: [],
    seenSabs: new Set(),
  };
  addEvents(v, g.events);
  if (!v.mine) fastForward(v, v.inputs.length - SPECTATOR_BUFFER);
  v.coinsHeard = v.sim.coins;
  v.beatHeard = v.sim.beaten;
  v.splatted = !v.sim.alive;
  return v;
}

function addEvents(v: View, evs: NetEvent[]): void {
  for (const e of evs) {
    const list = v.events.get(e.tick) ?? [];
    list.push({ id: e.id, kind: e.kind });
    v.events.set(e.tick, list);
  }
}

function flushInputs(): void {
  const v = view;
  if (!v?.mine || v.sent >= v.inputs.length) return;
  const from = v.sent;
  const to = v.inputs.length;
  const a = v.netEvents.filter((e) => e.tick >= from && e.tick < to);
  if (net.send({ t: 'in', f: from, i: v.inputs.slice(from, to), a: a.length ? a : undefined })) v.sent = to;
}

// --------------------------------------------------------------- simulation

function stepRecorded(v: View): void {
  const t = v.sim.tick;
  v.sim.step(v.inputs[t], v.events.get(t));
}

function fastForward(v: View, target: number): void {
  while (v.sim.tick < target && v.sim.alive) stepRecorded(v);
}

/** Our own run: simulate locally at wall-clock pace, record inputs for the server. */
function stepMine(v: View, now: number): void {
  if (v.over || !v.sim.alive || now < v.startAt) return;
  const target = Math.floor(((now - v.startAt) * TICK_HZ) / 1000);
  for (let n = 0; v.sim.tick < target && v.sim.alive && n < 240; n++) {
    const bits = input.bits();
    let evs: SabEvent[] | undefined;
    if (v.incoming.length) {
      evs = v.incoming.splice(0);
      for (const e of evs) v.netEvents.push({ tick: v.sim.tick, id: e.id, kind: e.kind });
    }
    v.sim.step(bits, evs);
    v.inputs.push(bits);
  }
  if (!v.sim.alive) flushInputs();
}

/** Someone else's run: replay their inputs with a small jitter buffer. */
function stepSpectator(v: View, now: number, dt: number): void {
  if (now < v.startAt || !v.sim.alive) return;
  const limit = v.over ? Math.min(v.inputs.length, v.over.tick) : v.inputs.length;
  const lag = limit - v.sim.tick;
  if (lag <= 0) {
    v.buffering = true;
    v.acc = 0;
    return;
  }
  if (!v.over && lag > 150) return fastForward(v, limit - SPECTATOR_BUFFER);
  if (v.buffering && !v.over && lag < SPECTATOR_BUFFER) return;
  v.buffering = false;
  const rate = v.over ? (lag > 30 ? 2 : 1) : lag > SPECTATOR_BUFFER * 2 ? 1.2 : lag < SPECTATOR_BUFFER / 2 ? 0.85 : 1;
  v.acc += (dt * TICK_HZ * rate) / 1000;
  let n = Math.min(Math.floor(v.acc), lag);
  v.acc -= Math.floor(v.acc);
  while (n-- > 0 && v.sim.alive) stepRecorded(v);
}

/** A recorded run: everything is known, it plays at wall-clock pace from its countdown. */
function stepReplay(v: View, now: number): void {
  if (now >= v.startAt) fastForward(v, Math.min(v.over!.tick, Math.floor(((now - v.startAt) * TICK_HZ) / 1000)));
}

/** Attract mode when nobody is playing: a bot plays a demo run. */
function stepIdle(now: number, dt: number): void {
  if (!idle || (!idle.sim.alive && now - idle.deadAt > 2000)) {
    const seed = (Math.random() * 2 ** 31) | 0;
    idle = { sim: new Sim(seed), brain: new Brain(0.7, seed), deadAt: 0, acc: 0 };
  }
  const s = idle.sim;
  if (!s.alive) return;
  idle.acc += (dt * TICK_HZ) / 1000;
  for (; idle.acc >= 1 && s.alive; idle.acc--) s.step(idle.brain.next(s));
  if (!s.alive) idle.deadAt = now;
}

// ------------------------------------------------------------------- render

let lastFrame = performance.now();
let lastUi = 0;

function frame(now: number): void {
  requestAnimationFrame(frame);
  const dt = Math.min(250, now - lastFrame);
  lastFrame = now;

  const v = view;
  if (v?.mine) stepMine(v, now);
  else if (replay) stepReplay(replay.view, now);
  else if (v) stepSpectator(v, now, dt);
  else stepIdle(now, dt);

  if (replay && !v?.mine) {
    const r = replay.view;
    const d = replay.data;
    cues(r, now);
    const o: DrawOpts = {
      key: `r${d.id}:${replay.n}`,
      player: r.label,
      mine: false,
      countdown: now < r.startAt ? (r.startAt - now) / 1000 : null,
      go: now >= r.startAt && now - r.startAt < GO_MS ? (now - r.startAt) / 1000 : null,
      caption: null,
      chaos: 0,
      live: false,
      replay: true,
      watchers: d.watchers,
      rivals: r.rivals.map((x) => ({ label: x.name === myName ? 'YOU' : withFlag(x.name, x.cc), dist: x.dist })),
    };
    if (isOver(r, now) && now - r.endedAt > DEATH_PAUSE) {
      const a = audience(d.watchers);
      o.caption = {
        title: DEATH_TITLE[d.reason] ?? 'GAME OVER',
        sub: `${r.label} ${DEATH_TEXT[d.reason] ?? 'is out'} at ${d.dist} m · ${d.score.toLocaleString()} pts`,
        note: `${a[0].toUpperCase()}${a.slice(1)} · ${ago(d.at)}`,
      };
    }
    renderer.draw(r.sim, o, now);
  } else if (v) {
    cues(v, now);
    const ended = isOver(v, now);
    const o: DrawOpts = {
      key: `g${v.gid}`,
      player: v.label,
      mine: v.mine,
      countdown: now < v.startAt ? (v.startAt - now) / 1000 : null,
      go: now >= v.startAt && now - v.startAt < GO_MS ? (now - v.startAt) / 1000 : null,
      caption: null,
      chaos: !ended ? chaos() : 0,
      live: !ended,
      watchers,
      rivals: v.rivals.map((r) => ({ label: r.name === myName ? 'YOU' : withFlag(r.name, r.cc), dist: r.dist })),
    };
    if (ended && !v.mine && now - v.endedAt > DEATH_PAUSE_SPECTATOR) {
      const reason = v.over?.reason ?? v.sim.deathReason ?? '';
      o.caption = { title: DEATH_TITLE[reason] ?? 'GAME OVER', sub: `${v.label} ${DEATH_TEXT[reason] ?? 'is out'} · ${v.over?.score ?? v.sim.score} pts` };
    }
    renderer.draw(v.sim, o, now);
  } else if (idle) {
    renderer.draw(idle.sim, {
      key: `idle${idle.sim.seed}`,
      player: 'DEMO',
      mine: false,
      countdown: null,
      go: null,
      // (Not under our own "your turn" prompt.)
      caption: turnUntil ? null : next ? { title: 'UP NEXT', sub: `${withFlag(next.name, next.cc)} is getting ready…` } : { title: "NOBODY'S PLAYING", sub: 'Join the line and show them how it’s done' },
      chaos: 0,
      live: false,
      watchers,
      rivals: [],
    }, now);
  }

  if (now - lastUi > 250) {
    lastUi = now;
    refresh();
  }
}
requestAnimationFrame(frame);

/** Sounds, in step with what's drawn: the countdown pops, coins (our own run only), passing a rival, the splat. */
function cues(v: View, now: number): void {
  const left = (v.startAt - now) / 1000;
  if (left > 0) {
    // Same moment the renderer pops the new number in.
    const n = Math.ceil(left);
    if (n <= 3 && n !== v.beeped && left % 1 > 0.7) {
      v.beeped = n;
      sound.tick();
    }
  } else if (v.beeped !== 0 && now - v.startAt < 300) {
    v.beeped = 0;
    sound.go();
  }
  if (v.mine && v.sim.coins > v.coinsHeard) sound.coin();
  v.coinsHeard = v.sim.coins;
  if (v.sim.beaten > v.beatHeard) sound.beat(v.mine ? 1 : 0.6);
  v.beatHeard = v.sim.beaten;
  if (!v.sim.alive && !v.splatted) {
    v.splatted = true;
    sound.splat(v.mine ? 1 : 0.7);
  }
}

/** Whether the run is over (and from when, for the death pause). */
function isOver(v: View, now: number): boolean {
  const ended = !v.sim.alive || (v.over !== null && v.sim.tick >= v.over.tick);
  if (ended && !v.endedAt) v.endedAt = now;
  return ended;
}

function chaos(): number {
  let best = 0;
  for (const k of SAB_KINDS) best = Math.max(best, votes.v[k] / votes.th);
  return best;
}

// ----------------------------------------------------------------------- UI

function refresh(): void {
  const now = performance.now();
  const v = view;
  const playing = !!v?.mine && !v.over && v.sim.alive;
  // Our own death: keep the game big (no chat jumping in) while the splat plays out.
  const dying = !!v?.mine && isOver(v, now) && now - v.endedAt < DEATH_PAUSE;
  ui.setMode(playing || dying);
  input.enabled = playing && now >= v!.startAt - 500;
  if (turnUntil && now > turnUntil + 2000) turnUntil = 0;

  // Overlay over the game screen.
  let ov: OverlayView | null = null;
  if (turnUntil && !playing) {
    const left = Math.max(0, Math.ceil((turnUntil - now) / 1000));
    ov = readySent
      ? { title: 'GET READY…' }
      : { title: "IT'S YOUR TURN!", text: `Everyone is watching. ${left}s to accept…`, action: { label: "I'M READY", icon: 'play', pulse: true, run: ready } };
  } else if (v?.mine && (v.over || !v.sim.alive) && !dying) {
    const r = v.over;
    const reason = r?.reason ?? v.sim.deathReason ?? '';
    const rank = boards.day.findIndex((e) => e.name === myName) + 1;
    const best = rank ? boards.day[rank - 1].score : 0;
    const score = r?.score ?? v.sim.score;
    ov = {
      title: DEATH_TITLE[reason] ?? 'GAME OVER',
      score: `${score.toLocaleString()} pts`,
      text: `You ${DEATH_TEXT[reason] ?? 'are out'} · ${v.sim.distance} m · ${v.sim.coins} coins` +
        (rank && best === score ? ` · #${rank} today!` : ''),
      action: pos === null ? { label: 'PLAY AGAIN', icon: 'play', run: join } : undefined,
      alt: r?.rid && lastRun?.rid === r.rid ? { label: 'SHARE', icon: 'share', run: () => share(lastRun!.rid, lastRun!.text) } : undefined,
    };
  }
  ui.setOverlay(ov);
  ui.setActions(screenActions(now));

  // Status line under the screen.
  let s: StatusView;
  if (netState !== 'open') s = { icon: 'hourglass', main: 'Reconnecting…', sub: 'Hang tight' };
  else if (playing || dying) s = { icon: 'eye', main: "You're live!", sub: `${watchers.toLocaleString()} people watching you`, tone: 'live' };
  else if (turnUntil) s = { icon: 'play', main: "IT'S YOUR TURN!", sub: 'Everyone is watching', tone: 'hot' };
  else if (pos === 1) s = { icon: 'play', main: "YOU'RE NEXT!", sub: v && !v.over ? 'As soon as this run ends…' : 'Any second now…', tone: 'hot' };
  else if (pos !== null && pos <= 3) s = { icon: 'person', main: `#${pos} in line. Almost there!`, sub: `Warm up those thumbs · ${eta(now)}` };
  else if (pos !== null) {
    s = { icon: 'person', main: `#${pos} of ${queueLen} in line`, sub: `${eta(now)} · meanwhile, sabotage the player`, action: { label: 'Leave', ghost: true, run: () => net.send({ t: 'leave' }) } };
  } else if (missed) s = { icon: 'hourglass', main: 'You missed your turn', sub: 'Tap “I’m ready” within 10 s next time', action: { label: 'Rejoin line', run: join } };
  else s = { icon: 'eye', main: 'Spectating', sub: queueLen ? `${queueLen} waiting to play` : 'Nobody in line: you could play right now!', action: { label: 'Join the line', run: join } };
  ui.setStatus(s);

  // (Not while watching a replay: the live run isn't on screen.)
  const canVote = netState === 'open' && !replay && !!v && !v.mine && !v.over && v.sim.alive && now >= v.startAt;
  ui.setVotes(votes, cdUntil - now, canVote, myVote);

  document.title =
    replay ? `${replay.data.name} ${DEATH_TEXT[replay.data.reason] ?? 'is out'} at ${replay.data.dist} m — GLOPE`
    : turnUntil && !readySent ? (Math.floor(now / 600) % 2 ? '▶ YOUR TURN! — GLOPE' : '▶▶▶ GO GO GO')
    : playing ? '● LIVE — GLOPE'
    : pos !== null ? `#${pos} in line — GLOPE`
    : 'GLOPE';
}

/** Replay controls while one plays; after someone else's run, a way to share it. */
function screenActions(now: number): ScreenAction[] | null {
  if (turnUntil) return null; // ("It's your turn" covers the screen; a replay keeps going under it until they accept)
  const live: ScreenAction = { label: 'Watch live', icon: 'dot', primary: true, run: closeReplay };
  if (replay) {
    const r = replay.view;
    const d = replay.data;
    const ended = isOver(r, now);
    const acts: ScreenAction[] = [];
    if (ended) acts.push({ label: 'Again', icon: 'again', run: restartReplay });
    else if (r.sim.tick < d.ticks - SKIP_TO_END_TICKS * 1.5) acts.push({ label: 'Skip to the end', icon: 'skip', run: skipReplay });
    acts.push({ label: 'Share', icon: 'share', run: () => share(d.id, `${withFlag(d.name, d.cc)} ${DEATH_TEXT[d.reason] ?? 'is out'} at ${d.dist} m on GLOPE`) });
    acts.push(live);
    return acts;
  }
  const v = view;
  if (v && !v.mine && v.over?.rid && isOver(v, now)) {
    const o = v.over;
    return [{ label: 'Share this run', icon: 'share', run: () => share(o.rid!, `${v.label} ${DEATH_TEXT[o.reason] ?? 'is out'} at ${o.dist} m ${audience(watchers)} on GLOPE`) }];
  }
  return null;
}

// ------------------------------------------------------------------ replays

async function openReplay(id: string): Promise<void> {
  try {
    const res = await fetch(`/api/replay/${id}`);
    if (!res.ok) throw new Error(String(res.status));
    const data = (await res.json()) as ReplayData;
    replay = { data, view: replayView(data), n: 0 };
  } catch {
    ui.toast('That replay doesn’t exist (anymore). Here’s the live game!');
    history.replaceState(null, '', '/');
  }
  refresh();
}

function replayView(d: ReplayData): View {
  // (Built empty: makeView would fast-forward a spectator to the end of what's known.)
  const v = makeView({ gid: -1, seed: d.seed, player: d.name, cc: d.cc, startIn: REPLAY_COUNTDOWN, inputs: [], events: [], rivals: d.rivals });
  v.inputs = d.inputs;
  addEvents(v, d.events);
  v.over = { g: -1, player: d.name, score: d.score, dist: d.dist, coins: d.coins, reason: d.reason, tick: d.ticks, rid: d.id };
  return v;
}

function restartReplay(): void {
  if (!replay) return;
  replay = { data: replay.data, view: replayView(replay.data), n: replay.n + 1 };
  unlockAudio();
  refresh();
}

function skipReplay(): void {
  const r = replay?.view;
  if (!r) return;
  // Moving the start back makes the next frame fast-forward there.
  r.startAt = performance.now() - ((replay!.data.ticks - SKIP_TO_END_TICKS) * 1000) / TICK_HZ;
  r.beeped = 0;
  unlockAudio();
  refresh();
}

/** Plays a recorded run (from the leaderboard or your last run), unless you're the one playing live. */
function watch(rid: string): void {
  if (view?.mine && !view.over && view.sim.alive) return;
  history.replaceState(null, '', `/r/${rid}`);
  unlockAudio();
  void openReplay(rid);
}

function closeReplay(): void {
  if (!replay) return;
  replay = null;
  history.replaceState(null, '', '/');
  refresh();
}

/** The phone's share sheet, or copy the link. */
async function share(rid: string, text: string): Promise<void> {
  const url = `${location.origin}/r/${rid}`;
  unlockAudio();
  if (navigator.share) {
    try {
      await navigator.share({ title: 'GLOPE', text, url });
      return;
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    ui.toast('Link copied! Paste it anywhere.');
  } catch {
    ui.toast(url);
  }
}

interface LastRun {
  rid: string;
  text: string;
  score?: number;
  dist?: number;
}

function loadLastRun(): LastRun | null {
  try {
    const r = JSON.parse(store.get('glope.lastRun') ?? 'null') as Record<string, unknown> | null;
    if (!r || typeof r.rid !== 'string' || typeof r.text !== 'string') return null;
    const out: LastRun = { rid: r.rid, text: r.text };
    if (typeof r.score === 'number' && typeof r.dist === 'number') Object.assign(out, { score: r.score, dist: r.dist });
    return out;
  } catch {
    return null;
  }
}

function ago(at: number): string {
  const m = Math.round((Date.now() - at) / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const days = Math.round(h / 24);
  return days < 30 ? `${days} day${days > 1 ? 's' : ''} ago` : new Date(at).toLocaleDateString();
}

function eta(now: number): string {
  const s = Math.max(0, Math.round((etaAt - now) / 1000));
  if (!etaAt) return 'soon';
  if (s < 5) return 'any moment now'; // (the run on now went long: the estimate assumed 30 s)
  if (s < 60) return `~${s}s`;
  if (s < 3600) return `~${Math.round(s / 60)} min`;
  return `~${(s / 3600).toFixed(1)} h`;
}

// -------------------------------------------------------------------- misc

function unlockAudio(): void {
  sound.unlock();
}
window.addEventListener('pointerdown', unlockAudio);

function attention(): void {
  navigator.vibrate?.([200, 100, 200, 100, 400]);
  sound.attention();
}

function randomId(): string {
  if (crypto.randomUUID) return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}

// Exposed for quick debugging from the console.
Object.assign(window, { glope: { get view() { return view; }, get replay() { return replay; } } });
